import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
// eslint-disable-next-line import/no-unresolved
import { useInfiniteHits } from 'react-instantsearch-next';

import useScreen from 'hooks/useScreen';

const getBlogUrl = url => {
  const blogPost = url
    .split('/')
    .filter(v => !!v)
    .pop();
  return `https://joybird.com/blog/${blogPost}`;
};

const ResultsHeader = ({ variant = 'desktop', children }) => (
  <div
    className={classNames('mx-0 my-2.5 text-base font-bold', {
      'hidden lg:block': variant === 'advanced-search',
      'text-gray': variant !== 'mobile',
      'text-brand block mt-[15px] mb-[7px]': variant === 'mobile',
    })}
  >
    {children}
  </div>
);

ResultsHeader.propTypes = {
  variant: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
};

const StaticPages = ({
  onPageClick,
  variant = 'desktop',
  forComponent,
  query = '',
  currentPage = '',
}) => {
  const { items: hits, showMore, isLastPage } = useInfiniteHits();

  const { windowWidth } = useScreen();
  const infiniteScrollThreshold = windowWidth <= 768 ? '2800px' : '2200px';

  const pagesList = hits
    .map(hit => {
      if (!hit?.url) {
        return null;
      }

      const isStoriesUrl = hit.url && hit.url.startsWith('/stories');

      const handleClick = () => {
        if (onPageClick) {
          onPageClick({
            url: hit.url,
            query,
            currentPage,
            isStoriesUrl,
          });
        }
      };

      let finalURL;
      if (isStoriesUrl) {
        finalURL = (
          <a onClick={handleClick} href={getBlogUrl(hit.url)}>
            {getBlogUrl(hit.url)}
          </a>
        );
      } else {
        finalURL = (
          <Link
            onClick={handleClick}
            to={(hit.url || '').startsWith('/') ? hit.url : `/${hit.url}`}
          >
            {hit.url}
          </Link>
        );
      }

      return (
        <div
          key={hit.objectID || hit.url}
          className="flex flex-col bg-gray-light8 p-8 mb-8 text-gray"
        >
          <div className="text-[1.2rem] leading-[2rem] no-underline tracking-[-.02em] mt-0 mx-0 mb-[1.1rem] font-normal text-gray">
            {hit.title || hit.name}
          </div>
          <div className="font-normal text-gray text-base leading-[1.5rem] flex flex-col items-start text-[.8rem]">
            {hit.meta_description || hit.description}
          </div>
          <div className="no-underline text-[.8rem] cursor-pointer [&_a]:text-brand">
            {finalURL}
          </div>
        </div>
      );
    })
    .filter(Boolean);

  const headerText = forComponent === 'mobile' ? 'Results' : 'Pages';

  if (!hits || hits.length === 0) {
    return (
      <div className="w-full flex items-center justify-center min-h-[20vh]">
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"
            role="status"
            aria-label="Loading"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ResultsHeader variant={variant}>{headerText}</ResultsHeader>

      <InfiniteScroll
        dataLength={pagesList.length}
        next={showMore}
        hasMore={!isLastPage}
        scrollThreshold={infiniteScrollThreshold}
        loader={<h4>Loading...</h4>}
      >
        {pagesList}
      </InfiniteScroll>
    </div>
  );
};

export default StaticPages;
